import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { IntlProvider, useIntl } from "react-intl";

import { LxButton as Button } from "@labxchange/ui-components/lib/LxButton";
import { LogEvent } from "@learningblocks/shared-components/lib/EventLogger";

import { setModal } from "./actions";
import { fetchTranslations } from "./locales/config";
import style from "./style.module.scss";

function getLocale() {
  const intl = useIntl();
  return intl.locale;
}

/**
 * PopoutFooter Component
 *
 * This component serves as a footer for the NoteBookComponent, providing
 * navigation buttons (previous and next) and event logging functionality.
 */
export function PopoutFooter(props) {
  const locale = getLocale();
  const [translations, setTranslations] = useState({
    locale: locale || "en",
    messages: {},
  });

  // Effect hook for fetching translations when the language changes
  useEffect(() => {
    fetchTranslations(locale).then((data) => {
      setTranslations(data);
    });
  }, [locale]);

  /**
   * MainComponent - The main render function for the PopoutFooter.
   * It displays the Previous and Next buttons with appropriate logging events.
   */
  const MainComponent = () => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const updateModal = (data) => {
      dispatch(setModal(data));
    };

    const endSimulation = () => {
      updateModal({
        display: true,
        type: "EndSimulation",
        size: "md",
        connect: -1,
      });
    };

    const nextButtonText = {
      id: "NEXT_BUTTON_TEXT",
      defaultMessage: "Next section",
      description: "Next button. Select to advance to the next section.",
    };

    const endButtonText = {
      id: "ENDS_BUTTON_TITLE",
      defaultMessage: "End simulation",
      description: "End simulation button. Select to end the simulation.",
    };
    const previousButtonText = {
      id: "PREV_BUTTON_TEXT",
      defaultMessage: "Previous section",
      description:
        "Previous button. Select to go back to the previous section.",
    };

    const translatedNextText = intl.formatMessage(nextButtonText);
    const translatedEndText = intl.formatMessage(endButtonText);
    const translatedPreviousText = intl.formatMessage(previousButtonText);

    return (
      <div
        className={style.popoutFooter}
        style={{
          justifyContent: props.isFirstChapter ? "flex-end" : "space-between",
        }}
        dir="auto"
      >
        {/* Logging Event and Previous Button */}
        {!props.isFirstChapter && (
          <LogEvent
            logEventProps={{
              event_type: `sim_${props.section}_prevsection_btn clicked`,
              event_schema: "track",
            }}
            actionProps={{ onClick: { action: "click" } }}
            elementType="button"
          >
            <Button
              variant="secondary"
              text={translatedPreviousText}
              onClickHandler={props.prevSection}
              trailingIcon="back"
            />
          </LogEvent>
        )}

        {/* Logging Event and Next Button */}
        {!props.isLastChapter && (
          <LogEvent
            logEventProps={{
              event_type: `sim_${props.section}_nextsection_btn clicked`,
              event_schema: "track",
            }}
            actionProps={{ onClick: { action: "click" } }}
            elementType="button"
          >
            <Button
              variant={props.isSectionComplete ? "primary" : "secondary"}
              text={
                props.isLastChapter ? translatedEndText : translatedNextText
              }
              onClickHandler={
                props.isLastChapter ? endSimulation : props.nextSection
              }
              trailingIcon={!props.isLastChapter ? "next" : null}
            />
          </LogEvent>
        )}
      </div>
    );
  };

  if (Object.keys(translations.messages).length > 0) {
    return (
      <IntlProvider {...translations}>
        <MainComponent {...props} />
      </IntlProvider>
    );
  }
  return <></>;
}

// PropTypes for the PopoutFooter Component
PopoutFooter.propTypes = {
  className: PropTypes.string, // CSS classname for styling
  section: PropTypes.string, // Section title for logging purposes
  nextSection: PropTypes.func, // Function to be called when the "Next" button is clicked
  prevSection: PropTypes.func, // Function to be called when the "Previous" button is clicked
  isSectionComplete: PropTypes.bool, // Indicates if the current section is complete
};
